import React, { useEffect, useRef, useState } from 'react';
import { Box, Divider, IconButton, Typography, useTheme } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { TripEventDto } from '@acorn-portal/shared/src/services/telematicsDriverTripsApi';
import TripEventItem from './trip-event-item';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';

/**
 * Props for the TripEventsList component.
 */
interface TripEventsListProps {
	/** Array of trip events to display. */
	events?: TripEventDto[];
	/** ID of the currently highlighted event (e.g., selected on map). */
	highlightedEventId?: string;
	/** Callback fired when an event is clicked/highlighted. */
	onEventHighlight?: (eventId: string) => void;
}

// Cache for geocoded addresses to avoid duplicate API calls
const geocodeCache = new Map<string, string>();

// Singleton geocoder instance
let geocoderInstance: google.maps.Geocoder | null = null;

/** Delay before the first geocode so the Maps script has time to load. */
const GEOCODE_START_DELAY_MS = 500;

/** Extract road name from geocode results */
const extractRoadName = (results: google.maps.GeocoderResult[]): string | null => {
	// Look for route (road name) in address components
	for (const result of results) {
		const routeComponent = result.address_components.find((c) => c.types.includes('route'));
		if (routeComponent) {
			return routeComponent.long_name;
		}
	}

	// Fallback: use first two parts of formatted address
	if (results[0]) {
		const parts = results[0].formatted_address.split(',');
		return parts.slice(0, 2).join(',').trim();
	}

	return null;
};

/** Reverse geocode coordinates to get road name */
const reverseGeocode = async (lat: number, lng: number): Promise<string | null> => {
	const cacheKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;

	// Check cache first
	if (geocodeCache.has(cacheKey)) {
		return geocodeCache.get(cacheKey) || null;
	}

	// Initialize geocoder if needed
	if (!geocoderInstance && globalThis.google?.maps?.Geocoder) {
		geocoderInstance = new google.maps.Geocoder();
	}

	if (!geocoderInstance) return null;

	return new Promise((resolve) => {
		geocoderInstance!.geocode({ location: { lat, lng } }, (results, status) => {
			if (status !== 'OK' || !results || results.length === 0) {
				resolve(null);
				return;
			}

			const roadName = extractRoadName(results);
			if (roadName) {
				geocodeCache.set(cacheKey, roadName);
			}
			resolve(roadName);
		});
	});
};

/** Check if event has valid location coordinates */
const hasValidLocation = (event: TripEventDto): boolean => {
	return event.location?.latitude !== undefined && event.location?.longitude !== undefined;
};

/**
 * Scrollable list of trip events with geocoding and accordion behavior.
 *
 * Displays driving events in a scrollable container with expandable details.
 * Features include:
 * - Reverse geocoding to show road names instead of coordinates
 * - Accordion behavior (one event expanded at a time)
 * - Auto-scroll to highlighted events (for map interaction sync)
 * - Info modal explaining what events are
 * - Empty state when no events recorded
 *
 * @component
 * @example
 * ```tsx
 * <TripEventsList
 *   events={tripEvents}
 *   highlightedEventId={selectedEventId}
 *   onEventHighlight={(eventId) => setSelectedEventId(eventId)}
 * />
 * ```
 */
const TripEventsList: React.FC<TripEventsListProps> = ({ events, highlightedEventId, onEventHighlight }) => {
	const theme = useTheme();
	const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
	const [roadNames, setRoadNames] = useState<Map<string, string>>(new Map());
	const [infoModalOpen, setInfoModalOpen] = useState(false);
	const eventRefs = useRef<Map<string, HTMLDivElement>>(new Map());

	// Resolve road names one at a time (avoids geocoder rate limits) and show each as it arrives.
	useEffect(() => {
		// Drop names from a previous events array; bail out if there is nothing to drop
		setRoadNames((previous) => (previous.size === 0 ? previous : new Map()));

		if (!events || events.length === 0) return;

		let isCancelled = false;

		const resolveRoadNames = async () => {
			for (const event of events) {
				if (isCancelled) return;
				if (!event.eventId || !hasValidLocation(event)) continue;

				const roadName = await reverseGeocode(event.location!.latitude!, event.location!.longitude!);
				if (isCancelled || !roadName) continue;

				const eventId = event.eventId;
				setRoadNames((previous) => new Map(previous).set(eventId, roadName));
			}
		};

		// Give the Maps script a moment to load before the first geocode
		const timer = setTimeout(resolveRoadNames, GEOCODE_START_DELAY_MS);

		return () => {
			isCancelled = true;
			clearTimeout(timer);
		};
	}, [events]);

	// Auto-scroll to highlighted event and expand it
	useEffect(() => {
		if (highlightedEventId) {
			setExpandedEventId(highlightedEventId);

			// Scroll to the highlighted event
			const eventElement = eventRefs.current.get(highlightedEventId);
			if (eventElement) {
				eventElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}
	}, [highlightedEventId]);

	const handleEventToggle = (eventId: string | undefined) => {
		if (!eventId) return;

		// Accordion behavior - only one expanded at a time
		setExpandedEventId((prev) => (prev === eventId ? null : eventId));

		// Notify parent of highlight (for map interaction)
		if (onEventHighlight && eventId) {
			onEventHighlight(eventId);
		}
	};

	const setEventRef = (eventId: string | undefined, element: HTMLDivElement | null) => {
		if (eventId && element) {
			eventRefs.current.set(eventId, element);
		}
	};

	if (!events || events.length === 0) {
		return (
			<Box
				sx={{
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: 2,
					backgroundColor: theme.palette.grey[100],
					p: 2,
				}}
			>
				<Typography variant='body2' color='text.secondary'>
					No events recorded for this trip.
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				border: `1px solid ${theme.palette.divider}`,
				borderRadius: 2,
				backgroundColor: theme.palette.grey[100],
				overflow: 'hidden',
			}}
		>
			{/* Header */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					p: 2,
					borderBottom: `1px solid ${theme.palette.divider}`,
				}}
			>
				<Typography
					variant='subtitle1'
					sx={{
						fontWeight: 'bold',
						color: theme.palette.text.primary,
					}}
				>
					Events
				</Typography>
				<IconButton size='small' onClick={() => setInfoModalOpen(true)} sx={{ p: 0 }} aria-label='Events information'>
					<InfoIcon
						sx={{
							fontSize: 20,
							color: theme.palette.success.main,
						}}
					/>
				</IconButton>
			</Box>

			{/* Info Modal */}
			<HelperModal
				open={infoModalOpen}
				onClose={() => setInfoModalOpen(false)}
				title='Events'
				description='Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.<br/><br/>Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.'
			/>

			{/* Events List */}
			<Box
				sx={{
					maxHeight: 450,
					overflowY: 'auto',
					'&::-webkit-scrollbar': {
						width: 6,
					},
					'&::-webkit-scrollbar-track': {
						backgroundColor: theme.palette.grey[100],
						borderRadius: 3,
					},
					'&::-webkit-scrollbar-thumb': {
						backgroundColor: theme.palette.grey[400],
						borderRadius: 3,
					},
				}}
			>
				{events.map((event, index) => (
					<Box key={event.eventId || index} ref={(el) => setEventRef(event.eventId, el as HTMLDivElement)}>
						<TripEventItem
							event={event}
							index={index}
							expanded={expandedEventId === event.eventId}
							onToggle={() => handleEventToggle(event.eventId)}
							highlighted={highlightedEventId === event.eventId}
							roadName={event.eventId ? roadNames.get(event.eventId) : undefined}
						/>
						{index < events.length - 1 && <Divider sx={{ borderColor: theme.palette.divider }} />}
					</Box>
				))}
			</Box>
		</Box>
	);
};

export default TripEventsList;
